import React, { useState, useCallback } from 'react'
import { useField } from 'formik'
import {
  isNil,
  filter,
  length,
  gt,
  isEmpty,
  __,
  addIndex,
  map,
  forEach,
  remove,
  trim,
  all,
  pipe,
  prop,
  equals,
} from 'ramda'
import { useIntl } from 'react-intl'
import SimpleInput from 'app/componentV4/Input/SimpleInput'
import DeleteOutlineOutlinedIcon from '@material-ui/icons/DeleteOutlineOutlined'
import { IconButton } from '@material-ui/core'
import { SUB_GROUP_MAX } from 'app/constants/group'
import { isEmptyOrNil } from 'libs/empty'
import LabelInput from 'app/componentV4/Labels/LabelInput'

const indexedMap = addIndex(map)

const SubGroupDynamicTextInput = ({
  className,
  name,
  disabled = false,
  onChange,
  id,
}) => {
  const [field, meta, helpers] = useField({ name })
  const { setValue } = helpers
  const { formatMessage } = useIntl()
  const fieldValue = field.value || [{ title: '' }]
  const [charLimitErrors, setCharLimitErrors] = useState([])
  const isError = !isNil(meta.error) && meta.touched && !disabled

  const isDataEmpty = all(pipe(prop('title'), trim, equals('')))(fieldValue)

  const handleChange = useCallback(
    (indexValue, event) => {
      const newErrors = [...charLimitErrors]
      const previous = fieldValue[indexValue]
      if (!isEmpty(previous.title)) {
        const previousDuplicate = indexedMap(
          (item, index) =>
            item.title === previous.title && index !== indexValue
              ? index
              : null,
          fieldValue,
        ).filter((index) => index !== null)

        if (previousDuplicate.length === 1) {
          newErrors[previousDuplicate[0]] = null
        }
      }

      const value = event.target.value
      const currentDuplicate = indexedMap(
        (item, index) =>
          !isEmpty(item.title) && item.title === value ? index : null,
        fieldValue,
      ).filter((index) => index !== null)

      if (currentDuplicate.length > 0) {
        forEach((index) => {
          newErrors[index] = formatMessage({
            id: 'groupsv4.add.dialog.subGroup.warning.match',
          })
        }, currentDuplicate)

        newErrors[indexValue] = formatMessage({
          id: 'groupsv4.add.dialog.subGroup.warning.match',
        })
      } else if (value.length > SUB_GROUP_MAX) {
        newErrors[indexValue] = formatMessage(
          { id: 'createNotification.dialog.validate.maxLength.message' },
          { maxLength: value.length - SUB_GROUP_MAX },
        )
      } else {
        newErrors[indexValue] = null
      }
      setCharLimitErrors(newErrors)

      const newInputs = [...fieldValue]
      newInputs[indexValue].title = value
      setValue(newInputs)

      if (onChange) {
        onChange(newInputs)
      }

      if (
        indexValue === fieldValue.length - 1 &&
        value !== '' &&
        fieldValue.length < SUB_GROUP_MAX
      ) {
        setValue([...newInputs, { title: '' }])
      }
    },
    [fieldValue, setValue, onChange, charLimitErrors],
  )

  const onDeleteItem = (index) => {
    setValue(remove(index, 1, fieldValue))
  }

  return (
    <div className="space-y-5">
      {fieldValue.map((inputValue, index) => {
        const isRequired = isDataEmpty && isEmptyOrNil(inputValue.title)
        const inputId = id
          ? `${id}-${index + 1}`
          : `subgroup-input-${index + 1}`
        return (
          <div className={className} key={`option-${index + 1}`}>
            <LabelInput
              label={formatMessage(
                { id: 'groupsv4.add.dialog.subgroup.title' },
                { subgroupIndex: index + 1 },
              )}
              required={isRequired}
              className="w-full"
            >
              <div className="flex items-start space-x-2">
                <SimpleInput
                  fullWidth
                  type="text"
                  id={inputId}
                  name={`option-${index + 1}`}
                  className="h-48 w-full mb-1 sm:mb-0 bg-white rounded-8 text-blue-950"
                  placeholder={formatMessage({
                    id: 'groupsv4.add.dialog.subGroup.placeholder',
                  })}
                  value={inputValue.title || ''}
                  onChange={(e) => handleChange(index, e)}
                  error={meta.error && !isEmptyOrNil(meta.error[index]?.title)}
                  helperText={meta.error && meta.error[index]?.title}
                  disabled={disabled}
                  aria-invalid={isError ? 'true' : 'false'}
                  endAdornment={
                    fieldValue.length > 1 ? (
                      <div className="cursor-pointer">
                        <IconButton disabled={disabled} tabIndex={-1}>
                          <DeleteOutlineOutlinedIcon
                            fontSize="small"
                            className="text-red-500"
                            disabled={disabled}
                            onClick={() => onDeleteItem(index)}
                          />
                        </IconButton>
                      </div>
                    ) : null
                  }
                />
              </div>
            </LabelInput>
          </div>
        )
      })}
    </div>
  )
}

export default SubGroupDynamicTextInput
